import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, UserPlus, Swords, Check, X, Copy, Send, Zap, Medal, ArrowUp, ArrowDown, Minus, Flame, Shield, Trophy } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Friends() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ friends: [], incoming: [], outgoing: [] });
  // Pilot callsign (display name) input. Replaces the legacy `email`
  // state — friend lookup is now name-based since pilots typically only
  // know each other's display names, not email addresses.
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  // Candidate list returned by the backend when the typed name matches
  // multiple pilots. UI renders each as a one-tap pick → POST
  // /friends/request-by-id.
  const [candidates, setCandidates] = useState([]);
  const [challengeMsg, setChallengeMsg] = useState("");
  const [created, setCreated] = useState(null); // { token, url }
  const [busy, setBusy] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(null); // friend_id being nudged
  const [nudgeLevel, setNudgeLevel] = useState(1);
  const [nudgeNote, setNudgeNote] = useState("");
  const [nudgeWager, setNudgeWager] = useState(0);
  const [walletBalance, setWalletBalance] = useState(null);
  const [squad, setSquad] = useState([]);
  const [topStreaks, setTopStreaks] = useState([]);
  const [topWagers, setTopWagers] = useState([]);
  const [wagerScope, setWagerScope] = useState("global");
  // Per-friend head-to-head rivalry record. Keyed by friend user_id;
  // each entry holds the full payload from /api/wagers/rivalry/{id}
  // (my_wins, their_wins, total_exchanged, last_5). Loaded after the
  // friends list resolves so the rendering can fall back to "—" while
  // pending and surface a compact W-L chip once loaded.
  const [rivalries, setRivalries] = useState({});
  const [rivalryOpen, setRivalryOpen] = useState(null);
  // Top-3 friends RIGHT ABOVE me on the squad scoreboard, with the
  // exact total_score delta needed to overtake them. Powers the new
  // "Rivalries · Climb the Squad" card.
  const [rivalsWatch, setRivalsWatch] = useState(null);
  // Squad Bracket modal — open/close + form state. Closed by default;
  // the CTA button in the page header opens it.
  const [squadBracketOpen, setSquadBracketOpen] = useState(false);
  const popoverRef = useRef(null);

  // Close the nudge popover when clicking outside.
  useEffect(() => {
    if (!nudgeOpen) return;
    const onDocClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setNudgeOpen(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [nudgeOpen]);

  const refresh = () => {
    api.get("/friends").then((r) => {
      setData(r.data);
      // Fetch rivalry summary for each friend in parallel — used to
      // render the small W-L chip + total ☾ exchanged on each row.
      const fids = (r.data.friends || []).map((f) => f.id);
      fids.forEach((fid) => {
        api.get(`/wagers/rivalry/${fid}`)
          .then((rr) => setRivalries((prev) => ({ ...prev, [fid]: rr.data })))
          .catch(() => {});
      });
    });
    api.get("/friends/leaderboard").then((r) => setSquad(r.data.entries || []));
    api.get("/friends/rivalries")
      .then((r) => setRivalsWatch(r.data))
      .catch(() => {});
    api.get("/leaderboard/top-streaks?limit=10").then((r) => setTopStreaks(r.data.items || [])).catch(() => {});
    // Wager leaderboard — scope toggle controlled by `wagerScope` state.
    const wagersUrl = wagerScope === "squad"
      ? "/wagers/leaderboard?friends_only=1"
      : "/wagers/leaderboard";
    api.get(wagersUrl).then((r) => setTopWagers(r.data.leaderboard || [])).catch(() => {});
  };

  // Lightweight: only refetch the squad leaderboard (skip the heavier /friends
  // call) on a 30s cadence so weekly-delta chips and live scores stay fresh.
  const refreshSquad = () => {
    api.get("/friends/leaderboard")
      .then((r) => setSquad(r.data.entries || []))
      .catch(() => {});
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refreshSquad, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wagerScope]);

  const sendRequest = async (e) => {
    e.preventDefault();
    setMsg("");
    setCandidates([]);
    setBusy(true);
    try {
      const r = await api.post("/friends/request", { name });
      setMsg(r.data.status === "accepted" ? "Connected!" : "Request sent");
      setName("");
      await refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // 409 with `candidates` — surface the disambiguation picker
      // instead of a flat error toast.
      if (
        err.response?.status === 409
        && detail
        && Array.isArray(detail.candidates)
      ) {
        setCandidates(detail.candidates);
        setMsg(detail.message || "Pick the right pilot.");
      } else {
        setMsg(formatApiErrorDetail(detail) || err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const sendRequestById = async (user_id) => {
    setMsg("");
    setBusy(true);
    try {
      const r = await api.post("/friends/request-by-id", { user_id });
      setMsg(r.data.status === "accepted" ? "Connected!" : "Request sent");
      setName("");
      setCandidates([]);
      await refresh();
    } catch (err) {
      setMsg(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const accept = async (id) => {
    try {
      await api.post("/friends/accept", { request_id: id });
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't accept request — try again.");
    }
  };
  const decline = async (id) => {
    try {
      await api.post("/friends/decline", { request_id: id });
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't decline request — try again.");
    }
  };
  const remove = async (user_id) => {
    try {
      await api.post("/friends/remove", { user_id });
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't remove friend — try again.");
    }
  };

  const openNudge = (friend_id, presets = {}) => {
    // Optional presets — used by the Rematch button on the rivalry
    // detail card to pre-fill the nudge popover with the previous
    // duel's level + a doubled-up wager. Server still clamps wager
    // to [0, 50] and balance, so a preset above the cap is safe.
    setNudgeOpen(friend_id);
    setNudgeLevel(presets.level ?? 1);
    setNudgeNote(presets.note ?? "");
    setNudgeWager(presets.wager ?? 0);
    // Pull current wallet balance so we can disable wager presets the
    // pilot can't afford.
    api.get("/moonstones/me")
      .then((r) => setWalletBalance(r.data.balance ?? 0))
      .catch(() => setWalletBalance(0));
  };

  const sendNudge = async (friend_id) => {
    const lvl = Math.max(1, Math.min(3000, parseInt(nudgeLevel || "1", 10) || 1));
    const wager = Math.max(0, Math.min(50, parseInt(nudgeWager || "0", 10) || 0));
    try {
      await api.post(`/friends/${friend_id}/nudge`, {
        level: lvl, message: nudgeNote, wager,
      });
      const f = data.friends.find((x) => x.id === friend_id);
      const wagerSuffix = wager > 0 ? ` · ${wager} ☾ on the line` : "";
      toast.success(`Challenge sent to ${f?.name || "pilot"}${wagerSuffix}`, {
        description: `Level ${lvl}${nudgeNote ? ` · "${nudgeNote.slice(0, 60)}"` : ""}`,
        icon: <Zap className="w-4 h-4 text-[#ffd700]" />,
        duration: 4000,
      });
      setNudgeOpen(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Nudge failed");
    }
  };

  const createChallenge = async () => {
    try {
      const r = await api.post("/challenges", { message: challengeMsg });
      const url = `${window.location.origin}/challenge/${r.data.token}`;
      setCreated({ token: r.data.token, url });
    } catch (err) {
      // Surface a friendly toast instead of letting the AxiosError bubble
      // up into CRA's red Uncaught Runtime Errors overlay.
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Couldn't generate challenge",
        { description: "Backend hiccup — give it a sec and try again." }
      );
    }
  };

  const copyChallengeUrl = () => {
    if (!created) return;
    navigator.clipboard.writeText(created.url);
    setMsg("Challenge link copied");
    setTimeout(() => setMsg(""), 1800);
  };

  // Friend Duel — fires a private 1v1 tournament with a generated
  // join_code, drops the link into the friend's chat-style nudge
  // collection, and surfaces the same join code as a toast for the
  // host to share manually if they want. Inherits ready-up + seeded
  // balls + outcome toasts from the standard tournament flow.
  const [duelBusy, setDuelBusy] = useState(null); // friend_id being challenged
  const duelFriend = async (friend) => {
    if (duelBusy) return;
    setDuelBusy(friend.id);
    try {
      const r = await api.post("/tournaments/create", {
        level: 1,
        buyin: 5,
        invite_only: true,
      });
      const tid = r.data?.id;
      const code = r.data?.join_code;
      // Push a nudge into the friend's notifications using the existing
      // friends/nudge endpoint as a pseudo-DM channel. Keeps the friend
      // experience consistent — they'll see the duel under "Challenges".
      try {
        await api.post(`/friends/${friend.id}/nudge`, {
          level: 1,
          note: `Join my private duel — code ${code} · /tournaments/${tid}`,
          wager: 0,
        });
      } catch {
        /* nudge is a nicety; the toast below is the canonical UI */
      }
      toast.success(`Friend Duel sent to ${friend.name}`, {
        description: `Private bracket · code: ${code}`,
        action: {
          label: "Open bracket",
          onClick: () => navigate(`/tournaments/${tid}`),
        },
        duration: 8000,
      });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't create duel",
      );
    } finally {
      setDuelBusy(null);
    }
  };

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main className="relative z-10 max-w-5xl mx-auto px-6 pt-28 pb-16 fade-in">
        <header className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="font-mono-cosmic text-xs tracking-[0.35em] text-[#00ffff] uppercase mb-2">
              // SQUAD
            </div>
            <h1 className="font-heading font-black uppercase text-4xl sm:text-5xl tracking-tighter">
              Friends & Challenges
            </h1>
            <p className="text-white/60 text-sm mt-1">
              Link up with fellow pilots and sling custom-seeded challenges at them.
            </p>
          </div>
          {/* Squad Bracket CTA — only visible when the pilot has at least
              one confirmed friend. Backend rejects the call otherwise so
              this is just a UX nicety. */}
          {(data.friends || []).length > 0 && (
            <button
              type="button"
              onClick={() => setSquadBracketOpen(true)}
              className="self-start sm:self-end flex items-center gap-2 px-4 py-2.5 border border-[#ffd700] bg-gradient-to-r from-[#ffd700]/15 to-[#ff007f]/15 hover:from-[#ffd700]/25 hover:to-[#ff007f]/25 text-[#ffd700] font-heading uppercase tracking-[0.2em] text-xs transition"
              data-testid="squad-bracket-cta"
              title="Create an invite-only bracket and DM the join code to your whole squad"
            >
              <Trophy className="w-4 h-4" />
              <span>Squad Bracket</span>
            </button>
          )}
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Add friend + challenge */}
          <div className="space-y-6">
            <div className="glass p-6">
              <div className="flex items-center gap-2 mb-4">
                <UserPlus className="w-4 h-4 text-[#00ffff]" />
                <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
                  Add Pilot
                </div>
              </div>
              {user && user.handle && (
                <div
                  className="mb-4 flex items-center justify-between gap-3 px-3 py-2 border border-[#b026ff]/40 bg-[#b026ff]/5"
                  data-testid="my-handle-chip"
                  title="Share this handle so other pilots can add you with zero ambiguity."
                >
                  <div className="min-w-0">
                    <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50 mb-0.5">
                      Your handle
                    </div>
                    <div className="font-mono-cosmic text-sm tracking-wider text-[#b026ff] truncate">
                      {user.handle}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(user.handle);
                      toast.success("Handle copied", {
                        description: "Share it so pilots can add you with zero ambiguity.",
                        duration: 3000,
                      });
                    }}
                    className="p-2 border border-[#b026ff]/40 text-[#b026ff] hover:bg-[#b026ff]/10 transition flex-none"
                    aria-label="Copy your handle"
                    data-testid="my-handle-copy-btn"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <form onSubmit={sendRequest} className="flex gap-2">
                <input
                  type="text"
                  placeholder="Pilot callsign or Callsign#1234"
                  className="input-cosmic flex-1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={70}
                  required
                  data-testid="friend-name-input"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="btn-neon disabled:opacity-50"
                  style={{ padding: "0.55rem 1rem" }}
                  data-testid="friend-send-btn"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
              {msg && (
                <div className="mt-3 font-mono-cosmic text-[11px] tracking-widest uppercase text-[#00ffff]">
                  {msg}
                </div>
              )}
              {candidates.length > 0 && (
                <div
                  className="mt-3 space-y-1.5"
                  data-testid="friend-candidates-list"
                >
                  {candidates.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => sendRequestById(c.id)}
                      disabled={busy}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 border border-white/15 hover:border-[#00ffff]/60 hover:bg-[#00ffff]/5 transition disabled:opacity-50"
                      data-testid={`friend-candidate-${c.id}`}
                      title={c.handle ? `Tip: type "${c.handle}" next time to skip this picker.` : undefined}
                    >
                      <span className="font-mono-cosmic text-xs tracking-wider text-white truncate flex items-baseline gap-1">
                        {c.name}
                        {c.tag && (
                          <span className="text-[10px] tracking-[0.15em] text-[#b026ff] tabular-nums" data-testid={`friend-candidate-tag-${c.id}`}>
                            #{c.tag}
                          </span>
                        )}
                      </span>
                      <span className="font-mono-cosmic text-[10px] tracking-[0.2em] uppercase text-white/50 tabular-nums flex-none">
                        {c.total_score.toLocaleString()} pts
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="glass p-6">
              <div className="flex items-center gap-2 mb-4">
                <Swords className="w-4 h-4 text-[#ff007f]" />
                <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
                  Challenge Anyone
                </div>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Generates a shareable link. Everyone who opens it plays the exact same seeded level.
              </p>
              <input
                type="text"
                placeholder="Optional message (e.g. Beat 2000 pts!)"
                className="input-cosmic mb-3"
                value={challengeMsg}
                maxLength={140}
                onChange={(e) => setChallengeMsg(e.target.value)}
                data-testid="challenge-message-input"
              />
              <button
                onClick={createChallenge}
                className="btn-neon"
                style={{ padding: "0.55rem 1rem" }}
                data-testid="challenge-create-btn"
              >
                <span className="inline-flex items-center gap-2">
                  <Swords className="w-4 h-4" /> Generate Challenge Link
                </span>
              </button>
              {created && (
                <div className="mt-4 border border-[#00ffff]/40 bg-black/50 p-3" data-testid="challenge-created">
                  <div className="font-mono-cosmic text-[10px] tracking-widest uppercase text-[#00ffff] mb-2">
                    SHARE THIS LINK
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 min-w-0 text-[11px] text-white/80 font-mono-cosmic truncate">
                      {created.url}
                    </code>
                    <button
                      onClick={copyChallengeUrl}
                      className="p-2 border border-white/15 hover:border-[#00ffff]"
                      data-testid="challenge-copy-btn"
                      aria-label="Copy link"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <Link
                      to={`/challenge/${created.token}`}
                      className="btn-neon cyan"
                      style={{ padding: "0.4rem 0.8rem", fontSize: 12 }}
                    >
                      Play
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Friends & requests */}
          <div className="space-y-6">
            {topStreaks.length > 0 && (
              <div className="glass p-5 border border-[#ff9500]/40" data-testid="top-streaks-leaderboard"
                style={{ boxShadow: "0 0 14px rgba(255,149,0,0.16)" }}>
                <div className="flex items-center gap-2 mb-3">
                  <Flame className="w-4 h-4 text-[#ff9500]" />
                  <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
                    Top Streak Pilots
                  </div>
                  <span className="ml-auto font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40">
                    public · global
                  </span>
                </div>
                <ul className="space-y-1.5">
                  {topStreaks.slice(0, 10).map((e) => (
                    <li
                      key={e.rank}
                      className="flex items-center gap-2 border border-white/10 hover:border-[#ff9500]/40 px-2 py-1.5 transition"
                      data-testid={`top-streak-row-${e.rank}`}
                    >
                      <span
                        className={`w-8 text-center font-heading font-black text-lg ${
                          e.rank === 1 ? "text-[#ffd700]" :
                          e.rank === 2 ? "text-[#c0c0c0]" :
                          e.rank === 3 ? "text-[#cd7f32]" :
                          "text-white/50"
                        }`}
                      >
                        {e.rank}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate text-white">{e.name}</div>
                        <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/40">
                          {e.shields > 0
                            ? `${e.shields} shield${e.shields === 1 ? "" : "s"} stocked`
                            : "no insurance"}
                        </div>
                      </div>
                      <div className="inline-flex items-center gap-1 font-mono-cosmic text-[11px] tracking-widest text-[#ff9500]">
                        <Flame className="w-3 h-3" />
                        {e.streak.toLocaleString()}d
                      </div>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/skins#shield-store"
                  className="mt-3 inline-flex items-center gap-1.5 font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#b026ff] hover:text-white transition"
                  data-testid="top-streaks-cta"
                >
                  <Shield className="w-3 h-3" /> Insure your streak →
                </Link>
              </div>
            )}

            {/* Top Wager Winners — global or squad-scoped ranking. Always
                renders so the SQUAD/ALL toggle is reachable. Empty-state
                copy shown when there are no qualifying winners. */}
            <div
              className="glass p-5 border border-[#ffd700]/40"
              style={{ boxShadow: "0 0 14px rgba(255,215,0,0.18)" }}
              data-testid="top-wagers-leaderboard"
            >
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-[#ffd700]" />
                  <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
                    Top Wager Winners
                  </div>
                  {/* Scope toggle — global vs friends-only */}
                  <div className="ml-auto flex items-center border border-white/15 text-[9px] tracking-[0.25em] font-mono-cosmic uppercase">
                    {[
                      { id: "global", label: "ALL" },
                      { id: "squad",  label: "SQUAD" },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setWagerScope(opt.id)}
                        className={`px-2 py-1 transition ${
                          wagerScope === opt.id
                            ? "bg-[#ffd700]/20 text-[#ffd700]"
                            : "text-white/45 hover:text-white"
                        }`}
                        data-testid={`top-wagers-scope-${opt.id}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <ul className="space-y-1.5">
                  {topWagers.length === 0 && (
                    <li className="text-white/45 font-mono-cosmic text-[10px] tracking-[0.2em] uppercase text-center py-3">
                      {wagerScope === "squad"
                        ? "No squad wager wins yet — start a duel!"
                        : "No wager wins yet — be the first."}
                    </li>
                  )}
                  {topWagers.slice(0, 10).map((w, i) => {
                    const rank = i + 1;
                    const handle = w.tag ? `${w.name}#${w.tag}` : w.name;
                    return (
                      <li
                        key={`${w.name}-${w.tag}`}
                        className="flex items-center gap-2 border border-white/10 hover:border-[#ffd700]/40 px-2 py-1.5 transition"
                        data-testid={`top-wager-row-${rank}`}
                      >
                        <span
                          className={`w-8 text-center font-heading font-black text-lg ${
                            rank === 1 ? "text-[#ffd700]" :
                            rank === 2 ? "text-[#c0c0c0]" :
                            rank === 3 ? "text-[#cd7f32]" :
                            "text-white/50"
                          }`}
                        >
                          {rank}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate text-white">{handle}</div>
                          <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/40">
                            {w.wagers_won_count} win{w.wagers_won_count === 1 ? "" : "s"}
                            {w.biggest_wager_won > 0 ? ` · biggest ${w.biggest_wager_won}` : ""}
                          </div>
                        </div>
                        <div className="inline-flex items-center gap-1 font-mono-cosmic text-[11px] tracking-widest text-[#ffd700] tabular-nums">
                          {w.wagers_won_amount} ☾
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

            <TopRivalriesPanel myId={user?.id} />

            {rivalsWatch?.rivals?.length > 0 && (
              <div
                className="glass p-5 border border-[#ff007f]/45"
                data-testid="rivalries-watch-card"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[#ff007f] text-base leading-none">⚔</span>
                  <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
                    Rivalries · Climb the Squad
                  </div>
                </div>
                <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/45 mb-3">
                  Points to overtake the next 3 pilots above you
                </div>
                <ul className="space-y-1.5">
                  {rivalsWatch.rivals.map((r, idx) => (
                    <li
                      key={r.id}
                      className="flex items-center gap-2 border border-[#ff007f]/25 hover:border-[#ff007f]/55 px-2 py-1.5 bg-[#ff007f]/5"
                      data-testid={`rivalry-row-${r.id}`}
                    >
                      <span className="w-8 text-center font-heading font-black text-base text-[#ff007f]">
                        ↑{idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate" title={r.handle || r.name}>
                          {r.name}
                        </div>
                        <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/45 tabular-nums">
                          {r.score.toLocaleString()} pts
                        </div>
                      </div>
                      <span className="font-mono-cosmic text-[10px] tracking-[0.2em] uppercase text-[#ffd700] tabular-nums">
                        +{r.delta.toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {squad.length > 1 && (
              <div className="glass p-5 border border-[#ffd700]/40" data-testid="squad-leaderboard">
                <div className="flex items-center gap-2 mb-3">
                  <Medal className="w-4 h-4 text-[#ffd700]" />
                  <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
                    Squad Leaderboard
                  </div>
                  <span className="ml-auto font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40" data-testid="squad-refresh-tag">
                    auto · 30s
                  </span>
                </div>
                <ul className="space-y-1.5">
                  {squad.map((e) => {
                    const delta = e.delta || 0;
                    const hasPrev = e.prev_rank != null && e.prev_rank !== undefined;
                    return (
                    <li
                      key={e.id}
                      className={`flex items-center gap-2 border px-2 py-1.5 ${
                        e.is_me
                          ? "border-[#00ffff]/60 bg-[#00ffff]/5"
                          : "border-white/10 hover:border-white/25"
                      }`}
                      data-testid={`squad-row-${e.id}`}
                    >
                      <span
                        className={`w-8 text-center font-heading font-black text-lg ${
                          e.rank === 1 ? "text-[#ffd700]" :
                          e.rank === 2 ? "text-[#c0c0c0]" :
                          e.rank === 3 ? "text-[#cd7f32]" :
                          "text-white/50"
                        }`}
                      >
                        {e.rank}
                      </span>
                      {/* Weekly rank delta chip — green ↑, pink ↓, dim · for unchanged.
                          Hidden when no prior snapshot exists (new squad members). */}
                      {hasPrev && (
                        <span
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 border font-mono-cosmic text-[9px] tracking-[0.15em] uppercase ${
                            delta > 0
                              ? "border-[#00ff41]/60 text-[#00ff41]"
                              : delta < 0
                              ? "border-[#ff007f]/60 text-[#ff007f]"
                              : "border-white/15 text-white/40"
                          }`}
                          title={`Weekly delta · was rank ${e.prev_rank}`}
                          data-testid={`squad-delta-${e.id}`}
                        >
                          {delta > 0 ? (
                            <><ArrowUp className="w-2.5 h-2.5" />{delta}</>
                          ) : delta < 0 ? (
                            <><ArrowDown className="w-2.5 h-2.5" />{Math.abs(delta)}</>
                          ) : (
                            <Minus className="w-2.5 h-2.5" />
                          )}
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm truncate ${e.is_me ? "text-[#00ffff]" : "text-white"}`}>
                          {e.name}{e.is_me ? " · You" : ""}
                        </div>
                        <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/40">
                          Level {e.unlocked_level.toLocaleString()} unlocked
                        </div>
                      </div>
                      <div className="font-mono-cosmic text-[11px] tracking-widest text-[#ffd700]">
                        {e.total_score.toLocaleString()}
                      </div>
                    </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="glass p-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-[#00ff41]" />
                <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
                  Squad ({data.friends.length})
                </div>
              </div>
              {data.friends.length === 0 ? (
                <div className="text-white/50 font-mono-cosmic text-xs tracking-widest uppercase">
                  No friends yet.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="friends-list">
                  {data.friends.map((f) => (
                    <li
                      key={f.id}
                      className="relative flex items-center justify-between border-b border-white/5 pb-2"
                    >
                      <div className="min-w-0">
                        <Link
                          to={`/u/${f.id}`}
                          className="text-white/90 hover:text-[#00ffff] text-sm truncate transition"
                          data-testid={`friend-name-${f.id}`}
                        >
                          {f.name}
                        </Link>
                        <div className="font-mono-cosmic text-[10px] tracking-widest text-[#00ffff] uppercase">
                          {f.total_score.toLocaleString()} pts
                        </div>
                        <RivalryChip
                          rivalry={rivalries[f.id]}
                          friendId={f.id}
                          isOpen={rivalryOpen === f.id}
                          onToggle={() =>
                            setRivalryOpen(rivalryOpen === f.id ? null : f.id)
                          }
                          onRematch={(level, wager) => {
                            setRivalryOpen(null);
                            openNudge(f.id, {
                              level,
                              wager,
                              note: `Rematch · L${level}`,
                            });
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-2 relative">
                        <button
                          onClick={() => duelFriend(f)}
                          disabled={duelBusy === f.id}
                          className="px-2.5 py-1 border border-[#ff007f]/55 text-[#ff007f] hover:bg-[#ff007f]/10 disabled:opacity-50 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center gap-1"
                          data-testid={`friend-duel-btn-${f.id}`}
                          title="Send a private 1v1 duel invite"
                        >
                          <Swords className="w-3 h-3" />
                          {duelBusy === f.id ? "…" : "Duel"}
                        </button>
                        <button
                          onClick={() => openNudge(f.id)}
                          className="px-2.5 py-1 border border-[#ffd700]/50 text-[#ffd700] hover:bg-[#ffd700]/10 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center gap-1"
                          data-testid={`friend-nudge-btn-${f.id}`}
                          title="Challenge on a level"
                        >
                          <Zap className="w-3 h-3" /> Nudge
                        </button>
                        <button
                          onClick={() => remove(f.id)}
                          className="text-white/40 hover:text-[#ff007f] text-xs font-mono-cosmic uppercase tracking-widest"
                        >
                          Remove
                        </button>
                        {nudgeOpen === f.id && (
                          <>
                            {/* mobile backdrop — desktop keeps inline popover */}
                            <div
                              className="fixed inset-0 z-[90] bg-black/60 sm:hidden"
                              onClick={() => setNudgeOpen(null)}
                            />
                            <div
                              ref={popoverRef}
                              className="fixed left-3 right-3 bottom-3 sm:absolute sm:right-0 sm:left-auto sm:top-9 sm:bottom-auto sm:w-72 glass border border-[#ffd700]/60 p-4 z-[100] sm:z-30"
                              style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.6)" }}
                              data-testid={`friend-nudge-popover-${f.id}`}
                            >
                            <div className="font-mono-cosmic text-[9px] tracking-[0.3em] text-[#ffd700] uppercase mb-2">
                              // NUDGE {f.name}
                            </div>
                            <label className="block mb-2">
                              <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/50 mb-1">
                                Level (1–3000)
                              </div>
                              <input
                                type="number"
                                min="1"
                                max="3000"
                                value={nudgeLevel}
                                onChange={(e) => setNudgeLevel(e.target.value)}
                                className="w-full bg-black/40 border border-white/15 focus:border-[#ffd700] px-2 py-1.5 text-white font-mono-cosmic text-xs outline-none"
                                data-testid="friend-nudge-level"
                              />
                            </label>
                            <label className="block mb-3">
                              <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/50 mb-1">
                                Message (optional)
                              </div>
                              <input
                                type="text"
                                maxLength={160}
                                value={nudgeNote}
                                placeholder="first to 10k?"
                                onChange={(e) => setNudgeNote(e.target.value)}
                                className="w-full bg-black/40 border border-white/15 focus:border-[#ffd700] px-2 py-1.5 text-white font-mono-cosmic text-xs outline-none"
                                data-testid="friend-nudge-note"
                              />
                            </label>
                            <div className="mb-3">
                              <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/50 mb-1 flex items-center justify-between">
                                <span>Wager (☾)</span>
                                {walletBalance !== null && (
                                  <span className="text-[#ffd700] tabular-nums normal-case tracking-normal">
                                    Balance: {walletBalance} ☾
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-5 gap-1 mb-1.5">
                                {[0, 5, 10, 25, 50].map((v) => {
                                  const tooBroke = walletBalance !== null && v > walletBalance;
                                  return (
                                    <button
                                      key={v}
                                      type="button"
                                      onClick={() => !tooBroke && setNudgeWager(v)}
                                      disabled={tooBroke}
                                      className={`py-1 border text-[10px] font-mono-cosmic tracking-[0.15em] transition ${
                                        nudgeWager === v
                                          ? "border-[#ffd700] bg-[#ffd700]/15 text-[#ffd700]"
                                          : tooBroke
                                            ? "border-white/10 text-white/25 cursor-not-allowed"
                                            : "border-white/15 text-white/55 hover:text-white"
                                      }`}
                                      data-testid={`nudge-wager-preset-${v}`}
                                    >
                                      {v === 0 ? "NONE" : v}
                                    </button>
                                  );
                                })}
                              </div>
                              <input
                                type="number"
                                min="0"
                                max="50"
                                value={nudgeWager}
                                onChange={(e) => setNudgeWager(e.target.value)}
                                className="w-full bg-black/40 border border-white/15 focus:border-[#ffd700] px-2 py-1.5 text-white font-mono-cosmic text-xs outline-none"
                                placeholder="custom 0–50"
                                data-testid="friend-nudge-wager-input"
                              />
                              {nudgeWager > 0 && (
                                <div className="text-[9px] text-[#ffd700]/80 font-mono-cosmic mt-1">
                                  Winner takes {nudgeWager} ☾ · refund after 7d if no response
                                </div>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                onClick={() => setNudgeOpen(null)}
                                className="py-1.5 border border-white/15 hover:text-white text-white/60 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition"
                                data-testid="friend-nudge-cancel"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => sendNudge(f.id)}
                                className="py-1.5 border border-[#ffd700] text-[#ffd700] hover:bg-[#ffd700]/10 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition"
                                data-testid="friend-nudge-send"
                              >
                                Send
                              </button>
                            </div>
                          </div>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {data.incoming.length > 0 && (
              <div className="glass p-6">
                <div className="font-heading uppercase tracking-[0.25em] text-sm text-white mb-4">
                  Incoming Requests
                </div>
                <ul className="space-y-2">
                  {data.incoming.map((r) => (
                    <li key={r.id} className="flex items-center justify-between border-b border-white/5 pb-2">
                      <span className="text-white/90 text-sm truncate">{r.name}</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => accept(r.id)}
                          className="p-1.5 border border-[#00ff41] text-[#00ff41] hover:bg-[#00ff41]/10"
                          aria-label="Accept"
                          data-testid={`accept-${r.id}`}
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => decline(r.id)}
                          className="p-1.5 border border-[#ff007f] text-[#ff007f] hover:bg-[#ff007f]/10"
                          aria-label="Decline"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.outgoing.length > 0 && (
              <div className="glass p-6">
                <div className="font-heading uppercase tracking-[0.25em] text-sm text-white mb-4">
                  Pending Outgoing
                </div>
                <ul className="space-y-2">
                  {data.outgoing.map((r) => (
                    <li key={r.id} className="text-white/70 text-sm flex justify-between">
                      <span className="truncate">{r.name}</span>
                      <span className="font-mono-cosmic text-[10px] tracking-widest text-white/40 uppercase">
                        Waiting
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <SquadBracketModal
          open={squadBracketOpen}
          onClose={() => setSquadBracketOpen(false)}
          friendCount={(data.friends || []).length}
        />
      </main>
    </div>
  );
}


/**
 * One-tap squad bracket modal — pick level + buy-in + size, the
 * backend creates an invite-only bracket and DMs the join code to
 * every confirmed friend.
 *
 * Compact form (single column on mobile, side-by-side selects on
 * desktop) so the host doesn't have to leave /friends.
 */
function SquadBracketModal({ open, onClose, friendCount }) {
  const [level, setLevel] = useState(1);
  const [buyin, setBuyin] = useState(5);
  const [size, setSize] = useState(4);
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/friends/squad-bracket", {
        level: Math.max(1, Math.min(3000, parseInt(level || "1", 10) || 1)),
        buyin,
        size,
      });
      const d = r.data;
      toast.success(`Squad Bracket created · ${d.invited_count} friend${d.invited_count === 1 ? "" : "s"} pinged`, {
        description: `Join code: ${d.join_code}`,
        duration: 5000,
      });
      onClose();
      // Deep-link the host straight to the bracket detail.
      setTimeout(() => {
        window.location.href = `/tournaments/${d.tournament_id}`;
      }, 400);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't create squad bracket");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="squad-bracket-modal-backdrop"
    >
      <div
        className="w-full max-w-md border-2 border-[#ffd700] bg-[#0a0510] p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="squad-bracket-modal"
      >
        <div className="flex items-center gap-2 mb-1">
          <Trophy className="w-5 h-5 text-[#ffd700]" />
          <div className="font-heading uppercase tracking-[0.2em] text-base text-[#ffd700]">
            Squad Bracket
          </div>
        </div>
        <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/55 mb-4">
          // Invite-only · auto-DM the code to {friendCount} friend{friendCount === 1 ? "" : "s"}
        </div>

        <label className="block mb-3">
          <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/60 mb-1.5">
            Level
          </div>
          <input
            type="number"
            min="1"
            max="3000"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="w-full bg-black/50 border border-white/20 focus:border-[#ffd700] px-3 py-2 text-white font-mono-cosmic text-sm tabular-nums outline-none"
            data-testid="squad-bracket-level"
          />
        </label>

        <div className="mb-3">
          <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/60 mb-1.5">
            Buy-in
          </div>
          <div className="grid grid-cols-4 gap-1">
            {[5, 10, 25, 50].map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => setBuyin(b)}
                className={`py-2 border text-xs font-mono-cosmic tracking-wider transition ${
                  buyin === b
                    ? "border-[#ffd700] bg-[#ffd700]/15 text-[#ffd700]"
                    : "border-white/15 text-white/55 hover:border-white/40"
                }`}
                data-testid={`squad-bracket-buyin-${b}`}
              >
                {b} ☾
              </button>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/60 mb-1.5">
            Bracket size
          </div>
          <div className="grid grid-cols-3 gap-1">
            {[4, 8, 16].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSize(s)}
                className={`py-2 border text-xs font-mono-cosmic tracking-wider transition ${
                  size === s
                    ? "border-[#3aa8ff] bg-[#3aa8ff]/15 text-[#3aa8ff]"
                    : "border-white/15 text-white/55 hover:border-white/40"
                }`}
                data-testid={`squad-bracket-size-${s}`}
              >
                {s} pilots
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border border-white/20 text-white/65 hover:bg-white/5 font-mono-cosmic text-xs tracking-[0.2em] uppercase transition"
            data-testid="squad-bracket-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="flex-1 py-2.5 border border-[#ffd700] bg-gradient-to-r from-[#ffd700]/20 to-[#ff007f]/20 hover:from-[#ffd700]/30 hover:to-[#ff007f]/30 font-heading uppercase tracking-[0.2em] text-xs text-[#ffd700] disabled:opacity-50 transition"
            data-testid="squad-bracket-submit"
          >
            {busy ? "Creating…" : `Pay ${buyin} ☾ & DM`}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Top Rivalries panel — global leaderboard of biggest H2H rivalries
 * by total moonstones exchanged in settled wagers.
 *
 * Loads on mount, no auto-refresh (data shifts slowly). Highlights
 * any row that includes the current pilot with a cyan border tint
 * for the "you appear in this rivalry" social hook.
 */
function TopRivalriesPanel({ myId }) {
  const [rivalries, setRivalries] = useState(null);
  useEffect(() => {
    api.get("/wagers/rivalries/top?limit=10")
      .then((r) => setRivalries(r.data.rivalries || []))
      .catch(() => setRivalries([]));
  }, []);
  if (rivalries === null) return null;  // initial load
  if (rivalries.length === 0) return null; // no settled rivalries anywhere yet
  return (
    <div
      className="glass p-5 border border-[#ff007f]/40"
      data-testid="top-rivalries-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <Swords className="w-4 h-4 text-[#ff007f]" />
        <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
          Top Rivalries
        </div>
        <span className="ml-auto font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40">
          biggest H2H · all-time
        </span>
      </div>
      <ul className="space-y-1.5">
        {rivalries.map((r, i) => {
          const rank = i + 1;
          const meIsIn = myId && (r.user_a.id === myId || r.user_b.id === myId);
          const handleA = r.user_a.tag ? `${r.user_a.name}#${r.user_a.tag}` : r.user_a.name;
          const handleB = r.user_b.tag ? `${r.user_b.name}#${r.user_b.tag}` : r.user_b.name;
          return (
            <li
              key={`${r.user_a.id}-${r.user_b.id}`}
              className={`flex items-center gap-2 border px-2 py-1.5 transition ${
                meIsIn
                  ? "border-[#00ffff]/60 bg-[#00ffff]/5"
                  : "border-white/10 hover:border-[#ff007f]/40"
              }`}
              data-testid={`top-rivalry-row-${rank}`}
            >
              <span
                className={`w-8 text-center font-heading font-black text-lg ${
                  rank === 1 ? "text-[#ffd700]" :
                  rank === 2 ? "text-[#c0c0c0]" :
                  rank === 3 ? "text-[#cd7f32]" :
                  "text-white/50"
                }`}
              >
                {rank}
              </span>
              <div className="flex-1 min-w-0 font-mono-cosmic text-[10px] tracking-[0.15em] uppercase truncate">
                <span className={meIsIn && r.user_a.id === myId ? "text-[#00ffff]" : "text-white/85"}>
                  {handleA}
                </span>
                <span className="text-[#ff007f] mx-1.5">⚔</span>
                <span className={meIsIn && r.user_b.id === myId ? "text-[#00ffff]" : "text-white/85"}>
                  {handleB}
                </span>
              </div>
              <div className="flex flex-col items-end font-mono-cosmic text-[10px] tracking-widest tabular-nums">
                <span className="text-[#ffd700]">
                  {r.total_exchanged} ☾
                </span>
                <span className="text-white/40 text-[9px]">
                  {r.duel_count} duel{r.duel_count === 1 ? "" : "s"}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}


/**
 * Compact head-to-head rivalry chip per friend row.
 *
 * Shows the W-L record + total ☾ exchanged inline. Tapping it toggles
 * an expanded card with the last-5 settled duels (winner, level,
 * amount). Renders nothing while the rivalry payload is still loading
 * (kept invisible vs a spinner so the row layout doesn't reflow).
 *
 * Edge cases handled:
 *   - 0 total duels → quiet "no duels yet" copy, no chip
 *   - rivalry undefined (still loading) → null
 */
function RivalryChip({ rivalry, friendId, isOpen, onToggle, onRematch }) {
  if (!rivalry) return null;
  const my = rivalry.my_wins || 0;
  const their = rivalry.their_wins || 0;
  const total = my + their + (rivalry.my_refunds || 0) + (rivalry.their_refunds || 0);
  if (total === 0) return null;

  const winning = my > their;
  const tied = my === their;
  const tone = tied ? "text-white/55" : winning ? "text-[#00ff88]" : "text-[#ff4d6d]";
  const border = tied
    ? "border-white/15"
    : winning
      ? "border-[#00ff88]/40 hover:border-[#00ff88]"
      : "border-[#ff4d6d]/40 hover:border-[#ff4d6d]";

  // Rematch target — most-recent settled duel. Wager doubles (capped
  // at 50, the server's hard wager ceiling). Refund-status duels still
  // qualify as "the last duel" for UX continuity; the doubled wager
  // logic just pulls forward whatever was last on the line.
  const last = (rivalry.last_5 || [])[0];
  const rematchLevel = last?.level;
  const rematchWager = last
    ? Math.min(50, Math.max(0, (last.wager || 0) * 2))
    : 0;

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={onToggle}
        className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 border ${border} ${tone} font-mono-cosmic text-[9px] tracking-[0.2em] uppercase transition`}
        data-testid={`friend-rivalry-chip-${friendId}`}
        title="Tap for last 5 duels"
      >
        <Swords className="w-2.5 h-2.5" />
        <span className="tabular-nums">
          {my}W-{their}L
        </span>
        {rivalry.total_exchanged > 0 && (
          <>
            <span className="opacity-50">·</span>
            <span className="tabular-nums">
              {rivalry.total_exchanged} ☾
            </span>
          </>
        )}
      </button>
      {isOpen && (
        <div
          className="mt-2 border border-white/15 bg-black/45 p-2 space-y-1"
          data-testid={`friend-rivalry-detail-${friendId}`}
        >
          <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/45 px-1 mb-1">
            // LAST {Math.min(5, (rivalry.last_5 || []).length)} DUELS
          </div>
          {(rivalry.last_5 || []).length === 0 ? (
            <div className="font-mono-cosmic text-[9px] tracking-[0.2em] uppercase text-white/35 px-1 py-1">
              No settled duels
            </div>
          ) : (
            (rivalry.last_5 || []).map((d, i) => {
              const won = d.winner_is_me === true;
              const lost = d.winner_is_me === false;
              const refunded = d.status === "settled_challenger_kept";
              const dotTone = refunded
                ? "bg-white/30"
                : won
                  ? "bg-[#00ff88]"
                  : "bg-[#ff4d6d]";
              return (
                <div
                  key={d.id || i}
                  className="flex items-center justify-between gap-2 px-1 py-0.5 font-mono-cosmic text-[9px] tracking-[0.15em] uppercase"
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`w-1.5 h-1.5 ${dotTone}`} />
                    <span className="text-white/65">L{d.level}</span>
                    {refunded ? (
                      <span className="text-white/45">refunded</span>
                    ) : (
                      <span className={lost ? "text-[#ff4d6d]" : "text-[#00ff88]"}>
                        {won ? "won" : "lost"}
                      </span>
                    )}
                  </div>
                  {d.wager > 0 && (
                    <span className="text-[#ffd700] tabular-nums">
                      {d.wager} ☾
                    </span>
                  )}
                </div>
              );
            })
          )}
          {/* Rematch CTA — only when there's a last duel to mirror.
              Pre-fills the nudge popover with the same level and a
              doubled-up wager (capped at 50). One-tap escalation. */}
          {last && onRematch && (
            <button
              type="button"
              onClick={() => onRematch(rematchLevel, rematchWager)}
              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 border border-[#ffd700]/60 text-[#ffd700] hover:bg-[#ffd700]/15 hover:border-[#ffd700] font-mono-cosmic text-[9px] tracking-[0.25em] uppercase transition"
              data-testid={`friend-rivalry-rematch-${friendId}`}
              title={
                rematchWager > 0
                  ? `Rematch on Level ${rematchLevel} · ${rematchWager} ☾ (double-or-nothing)`
                  : `Rematch on Level ${rematchLevel}`
              }
            >
              <Swords className="w-3 h-3" />
              <span>
                Rematch · L{rematchLevel}
                {rematchWager > 0 && (
                  <span className="ml-1 opacity-80">· {rematchWager} ☾</span>
                )}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
